import { useState } from "react";
import { ActionFunctionArgs, data, useLoaderData } from "react-router";

import { ActionPlan, ScenariosAndTrajectory } from "@/components/Trajectory";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { TTrajectoryAction } from "@/types";

export async function loader() {
  const response = await fetch("/api/client/trajectory_actions");
  if (response.status !== 200) {
    throw data("Failed to the trajectory category actions", {
      status: response.status,
    });
  }

  const { data: trajectoryActions } = (await response.json()) as {
    data: Array<TTrajectoryAction>;
  };

  const actions = trajectoryActions
    .filter((trajectoryAction) => trajectoryAction.status === "valid")
    .sort((a, b) => b.reduction_factor - a.reduction_factor); // Sort by reduction factor;

  const availableActions = trajectoryActions
    .filter((trajectoryAction) => trajectoryAction.status === "available")
    .sort((a, b) => b.reduction_factor - a.reduction_factor); // Sort by reduction factor

  // Put recommended actions first
  const recommendedActions = availableActions.filter(
    (availableAction) => availableAction.recommended
  );
  const notRecommendedActions = availableActions.filter(
    (availableAction) => !availableAction.recommended
  );

  return {
    actions,
    availableActions: [...recommendedActions, ...notRecommendedActions],
  };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const actionId = formData.get("actionId");
  const action = formData.get("action") as "add" | "remove" | null;
  const intensityPercentage = formData.get("intensityPercentage") as string;
  const difficulty = formData.get("difficulty") as string;
  // "valid"      -> currently added to the action plan
  // "available"  -> could be added to the action

  const body = {} as Record<string, string>;

  if (intensityPercentage) {
    body["intensity_percentage"] = intensityPercentage;
  }

  if (difficulty) {
    body["difficulty"] = difficulty;
  }

  if (action) {
    body["status"] = action === "add" ? "valid" : "available";
  }

  const response = await fetch(`/api/client/trajectory_actions/${actionId}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (response.status !== 200) {
    throw data("Failed to update a trajectory action.", {
      status: response.status,
    });
  }
}

function TrajectoryActionPlan() {
  const { actions, availableActions } = useLoaderData<typeof loader>();
  const { sendSegmentEvent } = useSegmentEvent();

  const [showTrajectory, setShowTrajectory] = useState(false);

  return (
    <div className="gap-x-sm grid grid-cols-[4fr_3fr]">
      {showTrajectory && <ScenariosAndTrajectory />}
      <ActionPlan
        isInEdition={!showTrajectory}
        onEditionChange={() => {
          const newShowTrajectory = !showTrajectory;
          setShowTrajectory(newShowTrajectory);
          sendSegmentEvent(
            newShowTrajectory
              ? "action-plan-validatation"
              : "action-plan-modification"
          );
        }}
        actions={actions}
        availableActions={availableActions}
      />
    </div>
  );
}

export default TrajectoryActionPlan;
